import React from "react";

import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { SnackbarProps } from "@mui/material";

interface DefaultToast {
  message: string | React.ReactNode;
  severity?: "info" | "success" | "warning" | "error";
  sticky?: boolean;
  /** if true, the toast will show a X button to dismiss the toast */
  dismissible?: boolean;
  /** if true, the toast will not hide if the user clicks somewhere else */
  disableClickAway?: boolean;
  anchorOrigin?: SnackbarProps["anchorOrigin"];
}

interface SystemToast extends DefaultToast {
  type: "system";
  title?: string;
  action?: React.ReactNode;
}

export type Toast = ({ type?: "default" } & DefaultToast) | SystemToast;

interface UseToasts {
  toast: Toast | undefined;
  showToast: (toast: Toast) => void;
  hideToast: () => void;
}

const useToasts = createWithEqualityFn<UseToasts>(
  (set) => ({
    toast: undefined,
    showToast: (toast) => set({ toast }),
    hideToast: () => set({ toast: undefined }),
  }),
  shallow
);

/** @deprecated for usage outside this file. can remove this tag when no more usages left outside this file */
export const showToast = (toast: Toast) => {
  useToasts.getState().showToast(toast);
};

/** @deprecated for usage outside this file. can remove this tag when no more usages left outside this file */
export const hideToast = () => {
  useToasts.getState().hideToast();
};

const normalizeToast = (toast: Toast | string): Toast => {
  if (typeof toast === "string") {
    return { message: toast };
  }
  return toast;
};

const createShowToast =
  (severity: Toast["severity"]) => (toast: Toast | string) => {
    showToast({ ...normalizeToast(toast), severity });
  };

export const toast = {
  hide: hideToast,
  show: showToast,
  info: createShowToast("info"),
  success: createShowToast("success"),
  warning: createShowToast("warning"),
  error: createShowToast("error"),
};

export default useToasts;
